import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Clock, TreePine } from "lucide-react";
import api from "@/lib/api";

const DAYS = ["Понедельник", "Вторник", "Среда", "Четверг", "Пятница", "Суббота", "Воскресенье"];
const HOURS = Array.from({ length: 24 }, (_, h) => h);
const fmt = (h) => `${String(h).padStart(2, "0")}:00`;

export default function ScheduleSettings() {
  const [days, setDays] = useState(null);
  const [letnik, setLetnik] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.get("/schedule").then((r) => {
      setDays(r.data.days);
      setLetnik(r.data.letnik_enabled);
    }).catch(() => {});
  }, []);

  const patch = (dow, field, value) =>
    setDays(days.map((d) => (d.dow === dow ? { ...d, [field]: value } : d)));

  const save = async () => {
    setSaving(true);
    setMsg("");
    try {
      await api.put("/admin/schedule", { days, letnik_enabled: letnik });
      setMsg("График сохранён");
    } catch (e) {
      setMsg(e.response?.data?.detail || "Ошибка сохранения");
    }
    setSaving(false);
  };

  if (!days) return null;

  return (
    <div className="bg-card border border-border p-6 mb-6 rounded-sm max-w-2xl" data-testid="schedule-settings">
      <h2 className="font-sans font-semibold text-sm uppercase tracking-widest mb-4 flex items-center gap-2">
        <Clock className="w-4 h-4 text-primary" /> График работы
      </h2>
      <p className="text-xs text-muted-foreground mb-4">
        Общий график для банных домов и летника. Клиенты видят его на сайте и не могут бронировать вне этих часов.
      </p>

      <div className="space-y-2">
        {days.map((d, i) => (
          <div key={d.dow} className="flex items-center gap-3 flex-wrap bg-background border border-border p-3" data-testid={`schedule-day-${d.dow}`}>
            <span className="text-sm w-32 shrink-0">{DAYS[i]}</span>
            <div className="flex items-center gap-2">
              <Switch
                checked={!d.closed}
                onCheckedChange={(v) => patch(d.dow, "closed", !v)}
                data-testid={`schedule-open-switch-${d.dow}`}
              />
              <span className={`text-xs ${d.closed ? "text-destructive" : "text-primary"}`}>
                {d.closed ? "Выходной" : "Работаем"}
              </span>
            </div>
            {!d.closed && (
              <div className="flex items-center gap-2 ml-auto">
                <select
                  value={d.open_hour}
                  onChange={(e) => patch(d.dow, "open_hour", Number(e.target.value))}
                  className="h-9 bg-card border border-border px-2 text-sm"
                  data-testid={`schedule-open-hour-${d.dow}`}
                >
                  {HOURS.map((h) => <option key={h} value={h}>{fmt(h)}</option>)}
                </select>
                <span className="text-muted-foreground text-sm">—</span>
                <select
                  value={d.close_hour}
                  onChange={(e) => patch(d.dow, "close_hour", Number(e.target.value))}
                  className="h-9 bg-card border border-border px-2 text-sm"
                  data-testid={`schedule-close-hour-${d.dow}`}
                >
                  {HOURS.map((h) => <option key={h} value={h}>{fmt(h)}</option>)}
                </select>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 mt-5 bg-background border border-border p-3">
        <TreePine className="w-4 h-4 text-primary" />
        <div className="flex-1">
          <p className="text-sm">Летник открыт (тапчаны и столики)</p>
          <p className="text-xs text-muted-foreground">Отключите на осень/зиму — летник исчезнет с сайта и бронь по нему будет недоступна</p>
        </div>
        <Switch checked={letnik} onCheckedChange={setLetnik} data-testid="schedule-letnik-switch" />
      </div>

      {msg && (
        <p className={`text-sm mt-4 ${msg.includes("сохран") ? "text-green-600" : "text-red-500"}`} data-testid="schedule-message">{msg}</p>
      )}
      <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground rounded-none mt-4" data-testid="schedule-save-btn">
        {saving ? "..." : "Сохранить график"}
      </Button>
    </div>
  );
}
